import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch, Link, useLocation } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import Home from "./pages/Home";
import Forecast from "./pages/Forecast";
import Locations from "./pages/Locations";
import NotificationSettings from "./pages/NotificationSettings";
import Profile from "./pages/Profile";
import Compare from "./pages/Compare";

// Auth pages
import { Login, Signup, VerifyEmail, ForgotPassword, ResetPassword } from "./pages/auth";

// Legal pages
import { PrivacyPolicy, TermsOfService } from "./pages/legal";

// Lazy loaded pages (heavy dependencies)
import { lazy, Suspense } from "react";

// MapExplorer and PhotoAnalyzer lazy loaded to reduce initial bundle size
// MapExplorer includes MapLibre (~150KB gzipped)
const MapExplorer = lazy(() => import("./pages/MapExplorer"));
const PhotoAnalyzer = lazy(() => import("./pages/PhotoAnalyzer"));

// Onboarding pages (lazy loaded)
const OnboardingProfile = lazy(() => import("./pages/onboarding/ProfileStep"));
const OnboardingLocation = lazy(() => import("./pages/onboarding/LocationStep"));
const OnboardingPreferences = lazy(() => import("./pages/onboarding/PreferencesStep"));
const OnboardingNotifications = lazy(() => import("./pages/onboarding/NotificationStep"));
const OnboardingComplete = lazy(() => import("./pages/onboarding/CompleteStep"));

import { useAuth } from "./hooks/useAuth";
import { useDeepLinking } from "./hooks/useDeepLinking";
import { Button } from "./components/ui/button";
import { Sunrise, MapPin, Heart, LogOut, Map, Camera, Bell, User, Menu, Loader2, TrendingUp } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

function Navigation() {
  const { user, isAuthenticated, logout, mockLogin, isLoading, isDemoModeAvailable } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [, setLocation] = useLocation();

  const handleLogout = async () => {
    try {
      await logout();
      toast.success("Logged out successfully");
    } catch (error) {
      toast.error("Failed to log out");
    }
  };

  return (
    <nav className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50" role="navigation" aria-label="Main navigation">
      <div className="container">
        <div className="flex h-16 items-center justify-between">
          <div className="flex items-center gap-8">
            <Link href="/" className="flex items-center gap-2 font-bold text-xl text-primary hover:text-primary/80 transition-colors">
              <Sunrise className="h-6 w-6" />
              SunSquatcher
            </Link>
            <div className="hidden md:flex items-center gap-6">
              <Link href="/forecast" className="text-sm font-medium text-foreground/80 hover:text-foreground transition-colors flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Forecast
              </Link>
              <Link href="/map" className="text-sm font-medium text-foreground/80 hover:text-foreground transition-colors flex items-center gap-2">
                <Map className="h-4 w-4" />
                Map Explorer
              </Link>
              <Link href="/photo-analyzer" className="text-sm font-medium text-foreground/80 hover:text-foreground transition-colors flex items-center gap-2">
                <Camera className="h-4 w-4" />
                Photo Analyzer
              </Link>
              {isAuthenticated && (
                <Link href="/notifications" className="text-sm font-medium text-foreground/80 hover:text-foreground transition-colors flex items-center gap-2">
                  <Bell className="h-4 w-4" />
                  Notifications
                </Link>
              )}
              {isAuthenticated && (
                <Link href="/locations" className="text-sm font-medium text-foreground/80 hover:text-foreground transition-colors flex items-center gap-2">
                  <Heart className="h-4 w-4" />
                  My Locations
                </Link>
              )}
              {isAuthenticated && (
                <Link href="/compare" className="text-sm font-medium text-foreground/80 hover:text-foreground transition-colors flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Compare
                </Link>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Mobile Menu Button */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" className="md:hidden" aria-label="Open mobile menu">
                  <Menu className="h-5 w-5" aria-hidden="true" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px] sm:w-[350px] safe-bottom flex flex-col">
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-2">
                    <Sunrise className="h-5 w-5 text-primary" />
                    SunSquatcher
                  </SheetTitle>
                </SheetHeader>
                <div className="mt-6 flex flex-col gap-1 sm:gap-2 flex-1 overflow-y-auto">
                  <Link href="/forecast" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start min-h-[44px] active:bg-accent/80 touch-manipulation" size="lg">
                      <MapPin className="h-5 w-5 mr-3" />
                      Forecast
                    </Button>
                  </Link>
                  <Link href="/map" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start min-h-[44px] active:bg-accent/80 touch-manipulation" size="lg">
                      <Map className="h-5 w-5 mr-3" />
                      Map Explorer
                    </Button>
                  </Link>
                  <Link href="/photo-analyzer" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start min-h-[44px] active:bg-accent/80 touch-manipulation" size="lg">
                      <Camera className="h-5 w-5 mr-3" />
                      Photo Analyzer
                    </Button>
                  </Link>
                  {isAuthenticated && (
                    <>
                      <Link href="/notifications" onClick={() => setMobileMenuOpen(false)}>
                        <Button variant="ghost" className="w-full justify-start min-h-[44px] active:bg-accent/80 touch-manipulation" size="lg">
                          <Bell className="h-5 w-5 mr-3" />
                          Notifications
                        </Button>
                      </Link>
                      <Link href="/locations" onClick={() => setMobileMenuOpen(false)}>
                        <Button variant="ghost" className="w-full justify-start min-h-[44px] active:bg-accent/80 touch-manipulation" size="lg">
                          <Heart className="h-5 w-5 mr-3" />
                          My Locations
                        </Button>
                      </Link>
                      <Link href="/compare" onClick={() => setMobileMenuOpen(false)}>
                        <Button variant="ghost" className="w-full justify-start min-h-[44px] active:bg-accent/80 touch-manipulation" size="lg">
                          <TrendingUp className="h-5 w-5 mr-3" />
                          Compare
                        </Button>
                      </Link>
                      <Link href="/profile" onClick={() => setMobileMenuOpen(false)}>
                        <Button variant="ghost" className="w-full justify-start min-h-[44px] active:bg-accent/80 touch-manipulation" size="lg">
                          <User className="h-5 w-5 mr-3" />
                          Profile
                        </Button>
                      </Link>
                      <div className="border-t border-border my-2"></div>
                      <Button
                        variant="outline"
                        className="w-full justify-start min-h-[44px] active:bg-accent/80 touch-manipulation"
                        size="lg"
                        onClick={() => {
                          handleLogout();
                          setMobileMenuOpen(false);
                        }}
                      >
                        <LogOut className="h-5 w-5 mr-3" />
                        Sign Out
                      </Button>
                    </>
                  )}
                  {!isAuthenticated && (
                    <>
                      <Link href="/login" onClick={() => setMobileMenuOpen(false)}>
                        <Button
                          variant="default"
                          className="w-full min-h-[44px] touch-manipulation"
                          size="lg"
                        >
                          Sign In
                        </Button>
                      </Link>
                      <Link href="/signup" onClick={() => setMobileMenuOpen(false)}>
                        <Button
                          variant="outline"
                          className="w-full min-h-[44px] touch-manipulation"
                          size="lg"
                        >
                          Create Account
                        </Button>
                      </Link>
                      {isDemoModeAvailable && (
                        <Button
                          variant="ghost"
                          className="w-full min-h-[44px] touch-manipulation"
                          size="lg"
                          onClick={() => {
                            mockLogin();
                            setMobileMenuOpen(false);
                          }}
                        >
                          Continue as Demo User
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </SheetContent>
            </Sheet>

            {/* Desktop Profile/Sign In */}
            <div className="hidden md:flex items-center gap-4">
            {isLoading ? (
              <div className="h-8 w-8 rounded-full bg-muted animate-pulse" />
            ) : isAuthenticated ? (
              <Link href="/profile">
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <Avatar className="h-6 w-6">
                    {user?.profilePicture && <AvatarImage src={user.profilePicture} alt={user.name || "Profile"} />}
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                      {user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:inline">{user?.name || "Profile"}</span>
                </Button>
              </Link>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="default" size="sm">
                    Sign In
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => setLocation('/login')}>
                    Sign In
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setLocation('/signup')}>
                    Create Account
                  </DropdownMenuItem>
                  {isDemoModeAvailable && (
                    <DropdownMenuItem onClick={mockLogin}>
                      Continue as Demo User
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}

// Loading fallback for lazy-loaded components
function LoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
    </div>
  );
}

function Router() {
  const [location] = useLocation();

  // Initialize deep linking handler
  useDeepLinking();

  // Auth routes don't show navigation
  const isAuthRoute = ['/login', '/signup', '/verify-email', '/forgot-password', '/reset-password'].includes(location);
  const isOnboardingRoute = location.startsWith('/onboarding');
  const isLegalRoute = ['/privacy', '/terms'].includes(location);

  return (
    <>
      {/* Skip to main content link for keyboard navigation */}
      <a href="#main-content" className="skip-to-content">
        Skip to main content
      </a>
      {!isAuthRoute && !isOnboardingRoute && !isLegalRoute && <Navigation />}
      <main id="main-content">
      <Switch>
        {/* Main app routes */}
        <Route path="/" component={Home} />
        <Route path="/forecast" component={Forecast} />
        <Route path="/map">
          <Suspense fallback={<LoadingFallback />}>
            <MapExplorer />
          </Suspense>
        </Route>
        <Route path="/photo-analyzer">
          <Suspense fallback={<LoadingFallback />}>
            <PhotoAnalyzer />
          </Suspense>
        </Route>
        <Route path="/notifications" component={NotificationSettings} />
        <Route path="/profile" component={Profile} />
        <Route path="/locations" component={Locations} />
        <Route path="/compare" component={Compare} />

        {/* Auth routes */}
        <Route path="/login" component={Login} />
        <Route path="/signup" component={Signup} />
        <Route path="/verify-email" component={VerifyEmail} />
        <Route path="/forgot-password" component={ForgotPassword} />
        <Route path="/reset-password" component={ResetPassword} />

        {/* Onboarding routes (lazy loaded) */}
        <Route path="/onboarding/profile">
          <Suspense fallback={<LoadingFallback />}>
            <OnboardingProfile />
          </Suspense>
        </Route>
        <Route path="/onboarding/location">
          <Suspense fallback={<LoadingFallback />}>
            <OnboardingLocation />
          </Suspense>
        </Route>
        <Route path="/onboarding/preferences">
          <Suspense fallback={<LoadingFallback />}>
            <OnboardingPreferences />
          </Suspense>
        </Route>
        <Route path="/onboarding/notifications">
          <Suspense fallback={<LoadingFallback />}>
            <OnboardingNotifications />
          </Suspense>
        </Route>
        <Route path="/onboarding/complete">
          <Suspense fallback={<LoadingFallback />}>
            <OnboardingComplete />
          </Suspense>
        </Route>

        {/* Legal pages */}
        <Route path="/privacy" component={PrivacyPolicy} />
        <Route path="/terms" component={TermsOfService} />

        <Route path="/404" component={NotFound} />
        <Route component={NotFound} />
      </Switch>
      </main>
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <TooltipProvider>
          <Toaster />
          <Router />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
